import { Map, OrderedSet } from 'immutable';

import { ICellExportPusherEventPayload, NotebookTypes } from '@mode/notebooks/contract';
import { ReadonlyCollection } from '@mode/shared/contract-common';
import {
  NotebookCellResource,
  NotebookVisualizationResource,
  ReportResource,
  ReportRunResource,
} from '@mode/legacy/data-access-model-js';
import { CellActionType } from '@mode/legacy/util-domain';

export namespace CellActions {
  export const Type = CellActionType;

  export interface LoadLayoutCellsSkipped {
    type: CellActionType.LoadLayoutVisualsSkipped;
  }

  export interface RunFailed {
    type: CellActionType.RunFailed;
    notebookToken: string;
    cellToken: string;
    error: Error;
  }

  export interface Idle {
    type: CellActionType.Idle;
    cellToken: string;
    runToken: string;
  }

  export interface Select {
    type: CellActionType.Select;
    cellToken: string;
    resultPane: boolean;
  }

  export interface BeginEdit {
    type: CellActionType.BeginEdit;
    cellToken: string;
    cellFrozen: boolean;
  }

  export interface EndEdit {
    type: CellActionType.EndEdit;
    cellToken: string;
  }

  export interface AddCell {
    type: CellActionType.AddCell;
    notebookToken: string;
    content?: string;
    contentType?: string;
    nextToken?: string;
  }

  export interface AddCellAndRun {
    type: CellActionType.AddCellAndRun;
    notebookToken: string;
    content: string;
    nextToken?: string;
  }

  export interface AddCellFailure {
    type: CellActionType.AddCellFailure;
    notebookToken: string;
    error: Error;
  }

  export interface AddCellSuccess {
    type: CellActionType.AddCellSuccess;
    notebookToken: string;
    cellResource: NotebookCellResource;
    nextToken?: string;
  }

  export interface ClearCellOutput {
    type: CellActionType.ClearCellOutput;
    cellToken: string;
  }

  export interface FoldCell {
    type: CellActionType.FoldCell;
    cellToken: string;
  }

  export interface AddToLayout {
    type: CellActionType.AddToLayout;
    cellToken: string;
  }

  export interface AddToLayoutSuccess {
    type: CellActionType.AddToLayoutSuccess;
    vizResource: NotebookVisualizationResource;
    cellToken: string;
  }

  export interface AddToLayoutFailure {
    type: CellActionType.AddToLayoutFailure;
    error: Error;
    cellToken: string;
  }

  export interface RemoveFromLayout {
    type: CellActionType.RemoveFromLayout;
    cellToken: string;
    vizToken: string;
  }

  export interface RemoveFromLayoutViaBuilder {
    type: CellActionType.RemoveFromLayoutViaBuilder;
    vizToken: string;
  }

  export interface RemoveFromLayoutFailure {
    type: CellActionType.RemoveFromLayoutFailure;
    cellToken: string;
    vizToken: string;
    error: Error;
  }

  export interface RemoveFromLayoutSuccess {
    type: CellActionType.RemoveFromLayoutSuccess;
    cellToken: string;
    vizToken: string;
  }

  export interface UpdateVizTitle {
    type: CellActionType.UpdateVizTitle;
    vizToken: string;
    title: string;
  }

  export interface UpdateVizTitleFailure {
    type: CellActionType.UpdateVizTitleFailure;
    vizToken: string;
    error: Error;
    vizResourcesArray: Map<string, NotebookVisualizationResource>;
  }

  export interface UpdateVizSuccess {
    type: CellActionType.UpdateVizSuccess;
    vizResource: NotebookVisualizationResource;
  }

  export interface EditCellContent {
    type: CellActionType.EditCellContent;
    content: string;
    cellToken: string;
  }

  export interface UpdateContentType {
    type: CellActionType.UpdateContentType;
    contentType: NotebookTypes.ContentType;
    cellToken: string;
  }

  export interface UpdateContentTypeFailure {
    type: CellActionType.UpdateContentTypeFailure;
    cellToken: string;
    original: NotebookCellResource;
    error: Error;
  }

  export interface MoveCell {
    type: CellActionType.MoveCell;
    cellToken: string;
    notebookToken: string;
    down: boolean;
  }

  export interface MoveCellFailure {
    type: CellActionType.MoveCellFailure;
    cellToken: string;
    nextCellToken: string; // The next cell token from before the update attempt
    notebookToken: string;
    error: Error;
  }

  export interface UpdateCell {
    type: CellActionType.UpdateCell;
    cellToken: string;
  }

  export interface UpdateCellFailure {
    type: CellActionType.UpdateCellFailure;
    cellToken: string;
    original: NotebookCellResource;
    error: Error;
  }

  export interface UpdateCellSuccess {
    type: CellActionType.UpdateCellSuccess;
    cellResource: NotebookCellResource;
  }

  export interface ToggleFreezeCell {
    type: CellActionType.ToggleFreezeCell;
    cellToken: string;
  }

  export interface ToggleFreezeCellFailure {
    type: CellActionType.ToggleFreezeCellFailure;
    cellToken: string;
    error: Error;
  }

  export interface DestroyCell {
    type: CellActionType.DestroyCell;
    cellToken: string;
    nextCellToken: string;
    notebookToken: string;
    notebookCellTokensArray: Map<string, OrderedSet<string>>;
  }

  export interface DestroyCellFailure {
    type: CellActionType.DestroyCellFailure;
    cellToken: string;
    notebookToken: string;
    error: Error;
  }

  export interface DestroyCellSuccess {
    type: CellActionType.DestroyCellSuccess;
    cellToken: string;
    runToken: string;
    notebookToken: string;
    resultTokens: OrderedSet<string>;
  }

  export interface LoadNotebookCellsSuccess {
    type: CellActionType.LoadNotebookCellsSuccess;
    notebookToken: string;
    cellResources: ReadonlyCollection<NotebookCellResource>;
  }

  export interface LoadNotebookCellsFailure {
    type: CellActionType.LoadNotebookCellsFailure;
    notebookToken: string;
    error: Error;
  }

  export interface LoadVizCellSuccess {
    type: CellActionType.LoadVizCellSuccess;
    vizToken: string;
    cellToken: string;
  }

  export interface LoadVizCellFailure {
    type: CellActionType.LoadVizCellFailure;
    vizToken: string;
    error: Error;
  }

  export interface LoadLayoutCells {
    type: CellActionType.LoadLayoutVisuals;
  }

  export interface StubLayoutCells {
    type: CellActionType.StubLayoutVisuals;
    vizResources: ReadonlyCollection<NotebookVisualizationResource>;
  }

  export interface PolledLayoutCells {
    type: CellActionType.PolledLayoutVisuals;
    report: ReportResource;
    reportRun: ReportRunResource;
  }

  export interface LoadLayoutCellsReady {
    type: CellActionType.LoadLayoutVisualsReady;
  }

  export interface LoadLayoutCellsSuccess {
    type: CellActionType.LoadLayoutVisualsSuccess;
    reportRun: ReportRunResource;
    vizResources: ReadonlyCollection<NotebookVisualizationResource>;
  }

  export interface LoadLayoutCellsFailure {
    type: CellActionType.LoadLayoutVisualsFailure;
    error: Error;
  }

  export interface LoadEmbeddedLayoutCellsSuccess {
    type: CellActionType.LoadEmbeddedLayoutVisualsSuccess;
    vizResources: ReadonlyCollection<NotebookVisualizationResource>;
  }

  // Same as CellExportActions.exportDataframeCompleted
  export interface ExportDataframeCompleted {
    type: CellActionType.ExportDataframeCompleted;
    payload: ICellExportPusherEventPayload;
  }

  export interface ExportCellComplete {
    type: CellActionType.ExportCellComplete;
    cellToken: string;
    queryToken: string;
    switchViewToken: string;
    localDatasetExportedAt: string;
  }

  export interface ExportedCellDelete {
    type: CellActionType.ExportedCellDelete;
    cellToken: string;
  }

  export interface RefreshDataViewAfterCellUpdate {
    type: CellActionType.RefreshDataViewAfterCellUpdate;
    cellToken: string;
  }
}

export type CellActions =
  | CellActions.RunFailed
  | CellActions.Idle
  | CellActions.Select
  | CellActions.BeginEdit
  | CellActions.EndEdit
  | CellActions.AddCell
  | CellActions.AddCellAndRun
  | CellActions.AddCellFailure
  | CellActions.AddCellSuccess
  | CellActions.ClearCellOutput
  | CellActions.FoldCell
  | CellActions.AddToLayout
  | CellActions.AddToLayoutSuccess
  | CellActions.AddToLayoutFailure
  | CellActions.RemoveFromLayout
  | CellActions.RemoveFromLayoutViaBuilder
  | CellActions.RemoveFromLayoutFailure
  | CellActions.RemoveFromLayoutSuccess
  | CellActions.UpdateVizTitle
  | CellActions.UpdateVizTitleFailure
  | CellActions.UpdateVizSuccess
  | CellActions.EditCellContent
  | CellActions.MoveCell
  | CellActions.MoveCellFailure
  | CellActions.UpdateCell
  | CellActions.UpdateCellFailure
  | CellActions.UpdateCellSuccess
  | CellActions.ToggleFreezeCell
  | CellActions.ToggleFreezeCellFailure
  | CellActions.UpdateContentType
  | CellActions.UpdateContentTypeFailure
  | CellActions.DestroyCell
  | CellActions.DestroyCellFailure
  | CellActions.DestroyCellSuccess
  | CellActions.LoadNotebookCellsSuccess
  | CellActions.LoadNotebookCellsFailure
  | CellActions.LoadLayoutCellsSkipped
  | CellActions.LoadVizCellSuccess
  | CellActions.LoadVizCellFailure
  | CellActions.LoadLayoutCells
  | CellActions.StubLayoutCells
  | CellActions.PolledLayoutCells
  | CellActions.LoadLayoutCellsReady
  | CellActions.LoadLayoutCellsSuccess
  | CellActions.LoadLayoutCellsFailure
  | CellActions.LoadEmbeddedLayoutCellsSuccess
  | CellActions.ExportCellComplete
  | CellActions.ExportedCellDelete
  | CellActions.ExportDataframeCompleted
  | CellActions.RefreshDataViewAfterCellUpdate;
